import type { ReactElement } from 'react';
import { useMyDesignsQuery } from '@/entities/my-design';
import { useMyQuotesQuery } from '@/entities/quote';
import { useAuthStore } from '@/entities/user';
import { routeForStatus } from '@/features/auth';
import { MY_DESIGNS_PATH, MY_QUOTES_PATH } from '@/shared/config';
import { getErrorMessage, getTrpcErrorCode } from '@/shared/lib';
import { OverviewNotice } from './overview-notice';
import { OverviewSection } from './overview-section';
import { OverviewStats } from './overview-stats';
import { QuickLinks } from './quick-links';
import { RecentDesigns } from './recent-designs';
import { RecentQuotes } from './recent-quotes';

const RECENT_COUNT = 3;
const ONBOARDING_PATH = '/onboarding';

/**
 * 마이페이지 홈 — 요약 줄, 최근 견적·최근 디자인, 바로가기.
 * 내 견적 첫 페이지(quotes.listMine)와 내 디자인 첫 페이지(designs.list)만 읽는다 — 목록 화면과 캐시를 함께 쓴다.
 */
export const MyPageOverview = (): ReactElement => {
  const quotesQuery = useMyQuotesQuery({ page: 1 });
  const designsQuery = useMyDesignsQuery();
  const accountStatus = useAuthStore((state) => state.user?.status);

  const quotes = quotesQuery.data
    ? { items: quotesQuery.data.items, total: quotesQuery.data.meta.total }
    : null;
  const firstDesignPage = designsQuery.data?.pages[0];
  const designs = firstDesignPage
    ? { count: firstDesignPage.items.length, hasMore: firstDesignPage.nextCursor !== null }
    : null;

  // 받은 값이 있으면 다시 조회(그림 기다림·창 포커스) 실패는 조용히 넘긴다 — 안내는 아직 받은 것이 없을 때만이다.
  const failedError =
    (quotesQuery.data === undefined ? quotesQuery.error : null) ??
    (designsQuery.data === undefined ? designsQuery.error : null);
  const isForbidden = failedError !== null && getTrpcErrorCode(failedError) === 'FORBIDDEN';
  const needsOnboarding =
    isForbidden && accountStatus !== undefined && routeForStatus(accountStatus) === ONBOARDING_PATH;

  return (
    <div className="flex min-w-0 flex-col gap-16 sm:gap-20">
      <div className="flex flex-col gap-6">
        {failedError !== null ? (
          <OverviewNotice
            message={
              isForbidden
                ? getErrorMessage(failedError, '인증을 마친 뒤 견적과 디자인을 볼 수 있습니다.')
                : '일부 정보를 불러오지 못했습니다.'
            }
            onboardingPath={needsOnboarding ? ONBOARDING_PATH : null}
            onRetry={() => {
              void quotesQuery.refetch();
              void designsQuery.refetch();
            }}
          />
        ) : null}
        <OverviewStats quotes={quotes} designs={designs} />
      </div>

      <div className="grid gap-16 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] xl:gap-14">
        <OverviewSection title="최근 견적" moreTo={MY_QUOTES_PATH} moreLabel="내 견적 전체">
          {quotesQuery.isPending ? (
            <p role="status" className="py-6 text-sm text-muted-foreground">
              최근 견적을 불러오는 중입니다.
            </p>
          ) : (
            <RecentQuotes rows={quotes?.items.slice(0, RECENT_COUNT) ?? []} />
          )}
        </OverviewSection>

        <OverviewSection title="최근 디자인" moreTo={MY_DESIGNS_PATH} moreLabel="내 디자인 전체">
          {designsQuery.isPending ? (
            <p role="status" className="py-6 text-sm text-muted-foreground">
              최근 디자인을 불러오는 중입니다.
            </p>
          ) : (
            <RecentDesigns designs={firstDesignPage?.items.slice(0, RECENT_COUNT) ?? []} />
          )}
        </OverviewSection>
      </div>

      <QuickLinks />
    </div>
  );
};
